import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'tb_risk_settings';

const DEFAULT_SETTINGS = {
  accountBalance: 10000,
  riskPerTrade: 1,
  maxDailyLoss: 2,
  maxWeeklyLoss: 5,
  maxConsecutiveLosses: 3,
  dailyTradeLimit: 5
};

export default function RiskManager() {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  // Load settings from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try { setSettings({...DEFAULT_SETTINGS, ...JSON.parse(stored)}); } catch {}
    }
  }, []);

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const fetchTrades = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('trades')
      .select('*')
      .eq('user_id', user.id)
      .order('trade_date', { ascending: false });
    setTrades(data || []);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
    const weekAgoStr = weekAgo.toISOString().slice(0, 10);

    const todayTrades = trades.filter(t => t.trade_date === today);
    const weekTrades = trades.filter(t => t.trade_date >= weekAgoStr);

    const todayR = todayTrades.reduce((s, t) => s + (parseFloat(t.r_multiple) || 0), 0);
    const weekR = weekTrades.reduce((s, t) => s + (parseFloat(t.r_multiple) || 0), 0);

    // Today: R * risk% = % of account lost/gained
    const todayPctChange = todayR * settings.riskPerTrade;
    const weekPctChange = weekR * settings.riskPerTrade;

    // Consecutive losses (from most recent backwards)
    let consecutiveLosses = 0;
    for (const t of trades) {
      if (t.outcome === 'Loss') consecutiveLosses++;
      else break;
    }

    // Dollar values
    const todayPnL = settings.accountBalance * (todayPctChange / 100);
    const weekPnL = settings.accountBalance * (weekPctChange / 100);
    const dailyLossLimit = settings.accountBalance * (settings.maxDailyLoss / 100);
    const weeklyLossLimit = settings.accountBalance * (settings.maxWeeklyLoss / 100);
    const riskPerTradeDollar = settings.accountBalance * (settings.riskPerTrade / 100);

    // Status flags
    const dailyLossHit = todayPctChange <= -settings.maxDailyLoss;
    const dailyLossWarning = !dailyLossHit && todayPctChange <= -(settings.maxDailyLoss * 0.7);
    const weeklyLossHit = weekPctChange <= -settings.maxWeeklyLoss;
    const consecutiveHit = consecutiveLosses >= settings.maxConsecutiveLosses;
    const tradeLimitHit = todayTrades.length >= settings.dailyTradeLimit;

    const canTrade = !dailyLossHit && !weeklyLossHit && !consecutiveHit && !tradeLimitHit;

    return {
      todayTrades, weekTrades, todayR, weekR, todayPctChange, weekPctChange,
      consecutiveLosses, todayPnL, weekPnL, dailyLossLimit, weeklyLossLimit,
      riskPerTradeDollar, dailyLossHit, dailyLossWarning, weeklyLossHit,
      consecutiveHit, tradeLimitHit, canTrade
    };
  }, [trades, settings]);

  if (loading) return <div style={{textAlign:'center',padding:32,color:'var(--text-3)'}}>Loading...</div>;

  function setField(k, v) {
    setSettings(s => ({ ...s, [k]: parseFloat(v) || 0 }));
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Risk <em>Manager</em></h1>
        <p className="page-subtitle">Set your daily limits — the system warns you before you violate your rules</p>
      </div>

      {/* TRADING STATUS BANNER */}
      <div className={`status-banner ${stats.canTrade ? 'green' : 'red'}`}>
        {stats.canTrade ? (
          <>
            <div className="status-banner-icon">✓</div>
            <div>
              <div className="status-banner-title">CLEARED TO TRADE</div>
              <div className="status-banner-sub">All risk parameters within limits.</div>
            </div>
          </>
        ) : (
          <>
            <div className="status-banner-icon">⛔</div>
            <div>
              <div className="status-banner-title">STOP TRADING — RISK LIMIT HIT</div>
              <div className="status-banner-sub">
                {stats.dailyLossHit && 'Daily loss limit reached. '}
                {stats.weeklyLossHit && 'Weekly loss limit reached. '}
                {stats.consecutiveHit && `${stats.consecutiveLosses} consecutive losses. `}
                {stats.tradeLimitHit && 'Daily trade limit reached. '}
                Step away until tomorrow.
              </div>
            </div>
          </>
        )}
      </div>

      {/* TODAY'S STATUS */}
      <div className="kpi-grid">
        <div className="kpi-cell">
          <div className="kpi-label">TODAY'S P&L</div>
          <div className="kpi-value" style={{color: stats.todayPnL >= 0 ? 'var(--green)' : 'var(--red)'}}>
            {stats.todayPnL >= 0 ? '+' : ''}${stats.todayPnL.toFixed(2)}
          </div>
          <div className="kpi-note">{stats.todayPctChange >= 0 ? '+' : ''}{stats.todayPctChange.toFixed(2)}%</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">TODAY'S R</div>
          <div className="kpi-value" style={{color: stats.todayR >= 0 ? 'var(--green)' : 'var(--red)'}}>
            {stats.todayR >= 0 ? '+' : ''}{stats.todayR.toFixed(2)}R
          </div>
          <div className="kpi-note">{stats.todayTrades.length} trades today</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">WEEK R</div>
          <div className="kpi-value" style={{color: stats.weekR >= 0 ? 'var(--green)' : 'var(--red)'}}>
            {stats.weekR >= 0 ? '+' : ''}{stats.weekR.toFixed(2)}R
          </div>
          <div className="kpi-note">{stats.weekTrades.length} trades this week</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">CONSECUTIVE LOSSES</div>
          <div className="kpi-value" style={{color: stats.consecutiveLosses >= settings.maxConsecutiveLosses ? 'var(--red)' : stats.consecutiveLosses >= 2 ? 'var(--amber)' : 'var(--text-1)'}}>
            {stats.consecutiveLosses}
          </div>
          <div className="kpi-note">max allowed: {settings.maxConsecutiveLosses}</div>
        </div>
      </div>

      {/* DAILY LIMIT PROGRESS BARS */}
      <div className="card mb-24">
        <div className="card-title">DAILY LIMIT TRACKER</div>

        <div className="risk-progress-block">
          <div className="risk-progress-header">
            <span>Daily Loss Limit</span>
            <span className={stats.dailyLossHit ? 'text-red' : stats.dailyLossWarning ? 'text-amber' : 'text-muted'}>
              {stats.todayPctChange < 0 ? `${stats.todayPctChange.toFixed(2)}%` : '0%'} / -{settings.maxDailyLoss}%
            </span>
          </div>
          <div className="risk-progress-track">
            <div className={`risk-progress-fill ${stats.dailyLossHit ? 'red' : stats.dailyLossWarning ? 'amber' : 'green'}`}
              style={{width: `${Math.min(100, Math.abs(Math.min(0, stats.todayPctChange)) / settings.maxDailyLoss * 100)}%`}}></div>
          </div>
        </div>

        <div className="risk-progress-block">
          <div className="risk-progress-header">
            <span>Weekly Loss Limit</span>
            <span className={stats.weeklyLossHit ? 'text-red' : 'text-muted'}>
              {stats.weekPctChange < 0 ? `${stats.weekPctChange.toFixed(2)}%` : '0%'} / -{settings.maxWeeklyLoss}%
            </span>
          </div>
          <div className="risk-progress-track">
            <div className={`risk-progress-fill ${stats.weeklyLossHit ? 'red' : 'green'}`}
              style={{width: `${Math.min(100, Math.abs(Math.min(0, stats.weekPctChange)) / settings.maxWeeklyLoss * 100)}%`}}></div>
          </div>
        </div>

        <div className="risk-progress-block">
          <div className="risk-progress-header">
            <span>Daily Trade Count</span>
            <span className={stats.tradeLimitHit ? 'text-red' : 'text-muted'}>
              {stats.todayTrades.length} / {settings.dailyTradeLimit}
            </span>
          </div>
          <div className="risk-progress-track">
            <div className={`risk-progress-fill ${stats.tradeLimitHit ? 'red' : 'green'}`}
              style={{width: `${Math.min(100, (stats.todayTrades.length / settings.dailyTradeLimit) * 100)}%`}}></div>
          </div>
        </div>
      </div>

      {/* SETTINGS */}
      <div className="card">
        <div className="card-title">YOUR RISK RULES</div>
        <p style={{fontSize:13,color:'var(--text-3)',marginBottom:16}}>
          Set these once. Saved in your browser. Risk per trade calculation uses your account balance.
        </p>
        <div className="journal-form">
          <div className="field">
            <label>ACCOUNT BALANCE ($)</label>
            <input type="number" step="100" value={settings.accountBalance} onChange={e => setField('accountBalance', e.target.value)} />
          </div>
          <div className="field">
            <label>RISK PER TRADE (%)</label>
            <input type="number" step="0.1" value={settings.riskPerTrade} onChange={e => setField('riskPerTrade', e.target.value)} />
            <span style={{fontSize:11,color:'var(--text-4)'}}>= ${stats.riskPerTradeDollar.toFixed(2)} per trade</span>
          </div>
          <div className="field">
            <label>MAX DAILY LOSS (%)</label>
            <input type="number" step="0.1" value={settings.maxDailyLoss} onChange={e => setField('maxDailyLoss', e.target.value)} />
            <span style={{fontSize:11,color:'var(--text-4)'}}>= ${stats.dailyLossLimit.toFixed(2)} stop-trading point</span>
          </div>
          <div className="field">
            <label>MAX WEEKLY LOSS (%)</label>
            <input type="number" step="0.1" value={settings.maxWeeklyLoss} onChange={e => setField('maxWeeklyLoss', e.target.value)} />
            <span style={{fontSize:11,color:'var(--text-4)'}}>= ${stats.weeklyLossLimit.toFixed(2)} weekly stop</span>
          </div>
          <div className="field">
            <label>MAX CONSECUTIVE LOSSES</label>
            <input type="number" step="1" value={settings.maxConsecutiveLosses} onChange={e => setField('maxConsecutiveLosses', e.target.value)} />
          </div>
          <div className="field">
            <label>MAX TRADES PER DAY</label>
            <input type="number" step="1" value={settings.dailyTradeLimit} onChange={e => setField('dailyTradeLimit', e.target.value)} />
          </div>
        </div>
      </div>
    </div>
  );
}
